import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { canViewMachiningEquipment, canManageMachiningEquipment } from '../../utils/roleConstants';
import { getStoredUser } from '../Tooling/tpmRequest';
import { authenticatedIdentity } from '../../_services/apiFetch';
import { machiningMasterDataService as service } from '../../_services/machiningMasterData.service';
import { AssetTable } from './AssetTable';
import { AssetForm } from './AssetForm';
import { AssetCategoryManager } from './AssetCategoryManager';
import { conditions, machiningFields } from './machiningFields';
import { exportMasterData } from './exportMasterData';

function AssetMasterDataContent({ kind, title, user, identity, syncAccount }) {
    const canManage = canManageMachiningEquipment(user);
    const [list, setList] = useState(null);
    const [categories, setCategories] = useState(null);
    const [query, setQuery] = useState({
        page: 1,
        limit: 25,
        search: '',
        category: '',
        assetCondition: '',
        active: '',
    });
    const [search, setSearch] = useState('');
    const [form, setForm] = useState(null);
    const [categoryManager, setCategoryManager] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);
    const lifecycle = useRef({
        mounted: false,
        generation: 0,
        busy: false,
        editing: false,
        denied: false,
    });
    const refreshRef = useRef(null);
    const editorTrigger = useRef(null);
    const queryKey = JSON.stringify(query);
    const definition = machiningFields[kind];

    function current(version) {
        const state = lifecycle.current;
        if (!state.mounted || state.denied) return false;
        if (identity !== authenticatedIdentity()) {
            syncAccount();
            return false;
        }
        return version === undefined || version === state.generation;
    }
    function failure(err, version) {
        if (!current()) return;
        if (err.status === 401 || err.status === 403) {
            lifecycle.current.denied = true;
            lifecycle.current.generation += 1;
            service.clear();
            setDenied(true);
            setList(null);
            setCategories(null);
            setForm(null);
            setCategoryManager(false);
            setError(err);
            setRefreshing(false);
        } else if (current(version)) setError(err);
    }

    useEffect(() => {
        const state = lifecycle.current;
        state.mounted = true;
        let disposed = false;
        let pending = false;
        let queued = false;

        function refresh(force = true, reconcileOnly = false) {
            if (disposed || !current()) return;
            if (reconcileOnly && !queued && !pending) return;
            // Progressive actions can invalidate an unfinished read; reconcile
            // after it settles, without resuming reads during editing or writes.
            if (pending) {
                queued = true;
                return;
            }
            if (state.busy || state.editing) return;
            queued = false;
            pending = true;

            const version = ++state.generation;

            setRefreshing(true);

            if (!reconcileOnly) setError(null);

            const assets = service.list(kind, JSON.parse(queryKey), force);
            const categoryRead = service.categories(kind, force);

            if (assets.data !== null) setList(assets.data);
            if (categoryRead.data !== null) setCategories(categoryRead.data);

            // Independent responses render progressively, with a shared generation
            // gate so a prior refresh can never overwrite an optimistic write.
            const reads = [
                assets.revalidate
                    .then((data) => {
                        if (!disposed && current(version)) setList(data);
                    })
                    .catch((err) => failure(err, version)),
                categoryRead.revalidate
                    .then((data) => {
                        if (!disposed && current(version)) setCategories(data);
                    })
                    .catch((err) => failure(err, version)),
            ];

            Promise.allSettled(reads).finally(() => {
                pending = false;
                if (disposed || !current()) return;
                setRefreshing(false);
                if (version !== state.generation) queued = true;
                refresh(true, true);
            });
        }

        refreshRef.current = refresh;
        setList(null);
        refresh(false);

        function refreshVisible() {
            syncAccount();

            if (document.visibilityState === 'visible' && navigator.onLine !== false) refresh();
        }

        const timer = window.setInterval(refreshVisible, 30000);

        window.addEventListener('focus', refreshVisible);
        window.addEventListener('online', refreshVisible);
        document.addEventListener('visibilitychange', refreshVisible);

        return () => {
            disposed = true;
            state.mounted = false;
            state.generation += 1;
            window.clearInterval(timer);
            window.removeEventListener('focus', refreshVisible);
            window.removeEventListener('online', refreshVisible);
            document.removeEventListener('visibilitychange', refreshVisible);
        };
    }, [kind, identity, queryKey]);

    useEffect(() => () => service.clear(), [kind, identity]);

    function closeEditor() {
        lifecycle.current.editing = false;
        lifecycle.current.generation += 1;
        setForm(null);
        setCategoryManager(false);
        setError(null);
        refreshRef.current?.(false);
        window.requestAnimationFrame(() => {
            if (current() && editorTrigger.current?.isConnected) editorTrigger.current.focus();
        });
    }

    async function openAsset(asset) {
        if (!current() || lifecycle.current.busy) return;

        editorTrigger.current = document.activeElement;
        lifecycle.current.editing = true;
        const version = ++lifecycle.current.generation;
        setError(null);

        if (!asset) {
            setForm({ asset: null });
            return;
        }

        setLoadingDetail(true);

        try {
            const detail = await service.detail(kind, asset.id);

            if (current(version)) setForm({ asset: detail });
        } catch (err) {
            failure(err, version);

            if (current(version)) lifecycle.current.editing = false;
        } finally {
            if (current(version)) setLoadingDetail(false);
        }
    }

    async function mutate(optimistic, send, saved, closeForm = false) {
        if (!current() || lifecycle.current.busy) return false;

        const state = lifecycle.current;
        state.busy = true;
        const version = ++state.generation;
        const previous = { list, categories };
        setSaving(true);
        setError(null);
        optimistic();
        let succeeded = false;
        let savedResult;

        try {
            const result = await send();

            if (current(version)) {
                saved(result);
                succeeded = true;
                savedResult = result;
                if (closeForm) closeEditor();
            }
        } catch (err) {
            if (current(version)) {
                setList(previous.list);
                setCategories(previous.categories);
            }

            failure(err, version);
        } finally {
            state.busy = false;

            if (current()) {
                setSaving(false);
                // Reconcile server pagination/filter results after asset saves.
                if (!state.editing) refreshRef.current?.(true, !succeeded);
            }
        }

        return succeeded ? savedResult : false;
    }

    const rows = list?.data || [];
    const savedCategories = (categories || []).filter((category) => category.id !== 'pending');
    const replaceRow = (source, id, replacement) =>
        source.map((row) => (String(row.id) === String(id) ? replacement : row));

    function saveAsset(values, keepOpen = false) {
        const asset = form.asset;
        const optimistic = asset
            ? { ...asset, ...values }
            : { ...values, id: 'pending', active: true };

        return mutate(
            () =>
                setList((previous) => ({
                    ...previous,
                    data: asset
                        ? replaceRow(previous.data, asset.id, optimistic)
                        : [optimistic, ...previous.data],
                    meta: { ...previous.meta, total: previous.meta.total + (asset ? 0 : 1) },
                })),
            () => (asset ? service.update(kind, asset.id, values) : service.create(kind, values)),
            (result) => {
                setList((previous) => ({
                    ...previous,
                    data: replaceRow(previous.data, optimistic.id, result),
                }));
                if (keepOpen) setForm({ asset: result });
            },
            !keepOpen
        );
    }

    function setActive(asset, active) {
        return mutate(
            () =>
                setList((previous) => ({
                    ...previous,
                    data: replaceRow(previous.data, asset.id, { ...asset, active }),
                })),
            () => service.setActive(kind, asset.id, active),
            (result) =>
                setList((previous) => ({
                    ...previous,
                    data: replaceRow(previous.data, asset.id, result),
                }))
        );
    }

    function saveCategory(existing, values) {
        const optimistic = existing
            ? { ...existing, ...values }
            : { ...values, id: 'pending', category: null, assetCount: 0 };

        return mutate(
            () => {
                setCategories((previous) =>
                    existing
                        ? replaceRow(previous, existing.id, optimistic)
                        : [...previous, optimistic]
                );

                // Only the legacy category description follows category renames.
                if (existing && kind === 'machines')
                    setList((previous) =>
                        previous
                            ? {
                                  ...previous,
                                  data: previous.data.map((row) =>
                                      String(row.category) === String(existing.category)
                                          ? { ...row, description: values.description }
                                          : row
                                  ),
                              }
                            : previous
                    );
            },
            () =>
                existing
                    ? service.updateCategory(kind, existing.id, values)
                    : service.createCategory(kind, values),
            (result) => setCategories((previous) =>
                replaceRow(previous, optimistic.id, { ...optimistic, ...result })
            )
        );
    }

    async function deleteCategory(category) {
        if (category.assetCount !== 0) return false;

        const result = await mutate(
            () => {},
            () => service.deleteCategory(kind, category.id),
            () => {
                setCategories(previous => previous.filter(item => item.id !== category.id));

                if (query.category === String(category.category)) {
                    setQuery(previous => ({ ...previous, page: 1, category: '' }));
                }
            }
        );

        return !!result;
    }

    async function exportCurrentPage() {
        const state = lifecycle.current;

        if (!current() || state.busy || state.editing || refreshing || !rows.length) {
            return;
        }

        const version = state.generation;
        state.busy = true;
        setExporting(true);
        setError(null);

        try {
            const assets = await Promise.all(rows.map((row) => service.detail(kind, row.id)));

            if (current(version)) {
                exportMasterData(kind, assets, savedCategories, user.companyName);
            }
        } catch (err) {
            failure(err, version);
        } finally {
            state.busy = false;

            if (current()) {
                setExporting(false);
            }
        }
    }

    const updateFilter = (field) => (event) =>
        setQuery((previous) => ({ ...previous, [field]: event.target.value, page: 1 }));
    const total = list?.meta?.total || 0;
    const pages = Math.max(1, Math.ceil(total / query.limit));
    const editing = !!form || categoryManager || loadingDetail || exporting;

    if (denied) return <Alert variant="danger">Access denied. {error?.message}</Alert>;

    return (
        <div className="container-fluid py-4">
            <h2>{title}</h2>
            <p className="text-muted">
                Manage {definition.plural} for{' '}
                {user.companyName || `company account ${user.accountId}`}. Incomplete details can be
                completed when editing an asset.
            </p>
            {error && !form && !categoryManager ? (
                <Alert variant="danger">{error.message}</Alert>
            ) : null}
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                <span role="status">
                    {list ? `${total} ${definition.plural}` : `Loading ${definition.plural}…`}
                    {categories === null ? ' · Loading categories…' : ''}
                    {refreshing && list ? ' · Refreshing…' : ''}
                    {saving ? ' · Saving…' : ''}
                    {loadingDetail ? ' · Loading details…' : ''}
                </span>
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-secondary"
                        disabled={refreshing || saving || editing}
                        onClick={() => refreshRef.current?.()}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outline-primary"
                        disabled={
                            !rows.length || categories === null || refreshing || saving || editing
                        }
                        onClick={exportCurrentPage}
                    >
                        {exporting ? 'Exporting…' : 'Export'}
                    </Button>
                    {canManage && <Button
                        variant="outline-primary"
                        disabled={categories === null || saving || editing}
                        onClick={(event) => {
                            editorTrigger.current = event.currentTarget;
                            lifecycle.current.editing = true;
                            lifecycle.current.generation += 1;
                            setError(null);
                            setCategoryManager(true);
                        }}
                    >
                        Manage categories
                    </Button>}
                    {canManage && <Button
                        disabled={
                            list === null || savedCategories.length === 0 || saving || editing
                        }
                        onClick={() => openAsset(null)}
                    >
                        Add {definition.singular}
                    </Button>}
                </div>
            </div>
            <Card>
                <Card.Body>
                    <Form
                        onSubmit={(event) => {
                            event.preventDefault();
                            setQuery((previous) => ({
                                ...previous,
                                search: search.trim(),
                                page: 1,
                            }));
                        }}
                    >
                        <Row className="g-3 mb-3">
                            <Col md={4}>
                                <Form.Group controlId="asset-search">
                                    <Form.Label>Search {definition.plural}</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            value={search}
                                            disabled={saving || editing}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder="No., name, asset or serial number"
                                        />
                                        <Button type="submit" disabled={saving || editing}>
                                            Search
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group controlId="asset-filter-category">
                                    <Form.Label>Filter category</Form.Label>
                                    <Form.Select
                                        value={query.category}
                                        disabled={categories === null || saving || editing}
                                        onChange={updateFilter('category')}
                                    >
                                        <option value="">All categories</option>
                                        {savedCategories.map((category) => (
                                            <option
                                                key={category.id}
                                                value={String(category.category)}
                                            >
                                                {category.description}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group controlId="asset-filter-condition">
                                    <Form.Label>Filter condition</Form.Label>
                                    <Form.Select
                                        value={query.assetCondition}
                                        disabled={saving || editing}
                                        onChange={updateFilter('assetCondition')}
                                    >
                                        <option value="">All conditions</option>
                                        {conditions.map((label, value) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group controlId="asset-filter-active">
                                    <Form.Label>Filter active</Form.Label>
                                    <Form.Select
                                        value={query.active}
                                        disabled={saving || editing}
                                        onChange={updateFilter('active')}
                                    >
                                        <option value="">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                    {list ? (
                        <AssetTable
                            kind={kind}
                            rows={rows}
                            categories={categories}
                            saving={saving || editing}
                            onEdit={openAsset}
                            onSetActive={setActive}
                            canManage={canManage}
                        />
                    ) : null}
                    {list ? (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <Form.Group
                                controlId="asset-page-size"
                                className="d-flex align-items-center gap-2"
                            >
                                <Form.Label className="mb-0 text-nowrap">Rows per page</Form.Label>
                                <Form.Select
                                    className="w-auto"
                                    value={query.limit}
                                    disabled={saving || editing}
                                    onChange={(event) =>
                                        setQuery((previous) => ({
                                            ...previous,
                                            page: 1,
                                            limit: Number(event.target.value),
                                        }))
                                    }
                                >
                                    {[25, 50, 100].map((limit) => (
                                        <option key={limit} value={limit}>
                                            {limit}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-secondary"
                                    disabled={query.page <= 1 || saving || editing}
                                    onClick={() =>
                                        setQuery((previous) => ({
                                            ...previous,
                                            page: previous.page - 1,
                                        }))
                                    }
                                >
                                    Previous
                                </Button>
                                <span>
                                    Page {query.page} of {pages}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    disabled={query.page >= pages || saving || editing}
                                    onClick={() =>
                                        setQuery((previous) => ({
                                            ...previous,
                                            page: previous.page + 1,
                                        }))
                                    }
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </Card.Body>
            </Card>
            {form ? (
                <AssetForm
                    kind={kind}
                    asset={form.asset}
                    categories={savedCategories}
                    company={user.companyName || `Company account ${user.accountId}`}
                    saving={saving}
                    error={error}
                    onClose={closeEditor}
                    onSave={saveAsset}
                    onDocumentError={failure}
                    readOnly={!canManage}
                />
            ) : null}
            {categoryManager ? (
                <AssetCategoryManager
                    kind={kind}
                    categories={categories || []}
                    saving={saving}
                    error={error}
                    onClose={closeEditor}
                    onSave={saveCategory}
                    onDelete={deleteCategory}
                />
            ) : null}
        </div>
    );
}

export function AssetMasterData({ kind, title }) {
    // Storage/focus callbacks trigger a render, and every render reads the latest
    // session so the App's same-window account updates reset protected content.
    const [, setStoredSession] = useState(() => localStorage.getItem('user'));
    const user = getStoredUser();
    const identity = authenticatedIdentity();

    function syncAccount() {
        setStoredSession(localStorage.getItem('user'));
    }

    useEffect(() => {
        window.addEventListener('storage', syncAccount);
        return () => window.removeEventListener('storage', syncAccount);
    }, []);

    if (!canViewMachiningEquipment(user)) return <Alert variant="danger">Access denied.</Alert>;
    
    return (
        <AssetMasterDataContent
            key={`${kind}:${identity}`}
            kind={kind}
            title={title}
            user={user}
            identity={identity}
            syncAccount={syncAccount}
        />
    );
}
